import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { entitiesSortedByCountry } from "utils/entitiesSortedByCountry";

export type DeadlineFilterType = {
  STATUS: string[];
  ENTITY: string[];
};

export type FilterProps = {
  values: DeadlineFilterType;
  updateFilter: <S extends keyof DeadlineFilterType>(
    name: S,
    newValue: DeadlineFilterType[S]
  ) => void;
};

const STATUS_OPTIONS = [
  { label: "Due", value: "DUE" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "In Progress", value: "ONGOING" },
  { label: "Filed", value: "COMPLETED" },
];

const AppliedFilters = ({ values, updateFilter }: FilterProps) => {
  const removeStatusFilter = (status: string) => {
    const appliedStatus = values.STATUS?.filter(
      (appliedStatus) => appliedStatus !== status
    );
    updateFilter("STATUS", appliedStatus || null);
  };

  const onRemoveEntityFilter = (entity: string) => {
    const appliedEntity = values.ENTITY?.filter(
      (appliedEntity) => appliedEntity !== entity
    );
    updateFilter("ENTITY", appliedEntity || null);
  };

  const { entities } = useCurrentGroupContext();
  const { usEntities } = entitiesSortedByCountry(entities);

  const fixedFilters = [
    {
      type: "STATUS",
      filters: [
        {
          label: "Due",
          value: "DUE",
        },
        {
          label: "Overdue",
          value: "OVERDUE",
        },
        {
          label: "Processing",
          value: "ONGOING",
        },
        {
          label: "Filed",
          value: "COMPLETED",
        },
      ],
    },
    {
      type: "ENTITY",
      filters: usEntities?.map((entity) => ({
        label: entity.name,
        value: entity.uuid,
      })),
    },
  ];

  const handleFilterClick = (type: keyof DeadlineFilterType, value: string) => {
    const currentSelection = values[type] || [];
    const isSelected = currentSelection.includes(value);

    if (isSelected) {
      updateFilter(
        type,
        currentSelection.filter((item) => item !== value)
      );
    } else {
      updateFilter(type, [...currentSelection, value]);
    }
  };
  return (
    <>
      {fixedFilters.map((filter) => {
        return filter.filters.map((item) => {
          const isSelected = values[
            filter.type as keyof DeadlineFilterType
          ]?.includes(item.value);
          return (
            <Chip
              key={item.value}
              isActive={isSelected}
              isFixedFilter
              onClick={() => {
                handleFilterClick(
                  filter.type as keyof DeadlineFilterType,
                  item.value
                );
              }}
              filterType={filter.type}
              onRemove={() => {
                if (filter.type === "STATUS") {
                  removeStatusFilter(item.value);
                } else {
                  onRemoveEntityFilter(item.value);
                }
              }}
            >
              {item.label}
            </Chip>
          );
        });
      })}
    </>
  );
};

export const DeadlineFilter = ({ values, updateFilter }: FilterProps) => {
  const { entities } = useCurrentGroupContext();
  const { usEntities } = entitiesSortedByCountry(entities);

  const entitiesOptions = usEntities.map((entity) => ({
    label: entity.name,
    value: entity.uuid,
  }));

  return (
    <Filter.Root
      defaultValue="STATUS"
      capsule={<AppliedFilters updateFilter={updateFilter} values={values} />}
    >
      <Filter.Portal size="small">
        <Filter.List>
          <Filter.ListItem value="STATUS">Status</Filter.ListItem>
          <Filter.ListItem value="ENTITY">Entity</Filter.ListItem>
        </Filter.List>
        <Filter.Body block value="ENTITY">
          <MultiSelectFilter
            selected={values.ENTITY || []}
            isSelectAll={false}
            isSearchable={false}
            options={entitiesOptions}
            onChange={(value) => {
              updateFilter("ENTITY", value);
            }}
          />
        </Filter.Body>
        <Filter.Body value="STATUS">
          <MultiSelectFilter
            selected={values.STATUS || []}
            isSelectAll={false}
            isSearchable={false}
            options={STATUS_OPTIONS}
            onChange={(value) => {
              updateFilter("STATUS", value);
            }}
          />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
